import { AxiosError } from "axios";

import { UseQueryOptions, useQuery } from "@enkor/react-query";

import BaseApi from "@logics/api/baseApi";
import {
  ResponseEventDetailV1,
  ResponseEventCommentV1,
  ResponseEventPreviewReview,
  ResponseEventDetailMyReview,
  ResponseEventUserDetail,
  ResponseEventLastNoShow,
} from "@models/data-contracts";
import { ApiResponseDto, ErrorResponseType } from "@models/client";

import { eventQueryKeys } from "../constants/eventQueryKeys";
import EventViewModel from "../viewModel/EventViewModel";
import CommentViewModel from "../viewModel/CommentViewModel";
import ReviewViewModel from "../viewModel/ReviewViewModel";
import AttendedUserViewModel from "../viewModel/AttendedUserViewModel";

const eventApi = new BaseApi("/event");

// ------------------------------------------------------------------------

export const useFetchEventDetailQuery = (eventId?: string) => {
  return useQuery({
    queryKey: eventQueryKeys.getEventDetail({ eventId }),
    queryFn: () =>
      eventApi.get<ApiResponseDto<ResponseEventDetailV1>>(`/${eventId}`),
    select: (response) => EventViewModel.create(response.data),
    retry: (failureCount, error) => {
      const DEFAULT_RETRY_COUNT = 3;
      const { response } = error as AxiosError<ErrorResponseType>;
      // NOTE : EV006 에러는 재시도 없이 바로 이벤트 리스트 페이지 이동.
      if (response?.data?.errorCode === "EV006") return false;

      if (failureCount >= DEFAULT_RETRY_COUNT) return false;

      return true;
    },
    enabled: !!eventId,
  });
};

// ------------------------------------------------------------------------

export const useFetchEventCommentListQuery = (eventId?: string) => {
  return useQuery({
    queryKey: eventQueryKeys.getEventComments({ eventId }),
    queryFn: () =>
      eventApi.get<ApiResponseDto<ResponseEventCommentV1[]>>(
        `/${eventId}/comment`,
      ),
    select: ({ data }) => ({
      hasComment: data.length !== 0,
      commentList: data.map((res) => CommentViewModel.create(res)),
    }),
    enabled: !!eventId,
  });
};

// ------------------------------------------------------------------------

export const useFetchHostReviewListQuery = (
  eventId?: string,
  options?: Omit<
    UseQueryOptions<ApiResponseDto<ResponseEventPreviewReview>>,
    "queryKey" | "queryFn" | "select" | "enabled"
  >,
) => {
  return useQuery({
    queryKey: eventQueryKeys.getHostReviewList({ eventId }),
    queryFn: () =>
      eventApi.get<ApiResponseDto<ResponseEventPreviewReview>>(
        `/${eventId}/detail/review`,
      ),
    select: ({ data }) => ReviewViewModel.create(data),
    enabled: !!eventId,
    ...options,
  });
};

// ------------------------------------------------------------------------

export const useFetchMyReviewQuery = (
  eventId?: string,
  options?: Omit<
    UseQueryOptions<ApiResponseDto<ResponseEventDetailMyReview>>,
    "queryKey" | "queryFn" | "select"
  >,
) => {
  return useQuery({
    queryKey: eventQueryKeys.getMyReview({ eventId }),
    queryFn: () =>
      eventApi.get<ApiResponseDto<ResponseEventDetailMyReview>>(
        `/${eventId}/detail/my-review`,
      ),
    select: ({ data }) => data,
    ...options,
  });
};

// ------------------------------------------------------------------------

export const useFetchEventUserListQuery = (eventId?: string) => {
  return useQuery({
    queryKey: eventQueryKeys.getEventUserList({ eventId }),
    queryFn: () =>
      eventApi.get<ApiResponseDto<ResponseEventUserDetail>>(`/${eventId}/user`),
    select: (response) => ({
      eventInfo: response.data.eventInfo,
      userList: response.data.userList.map((user) =>
        AttendedUserViewModel.create(user),
      ),
    }),
    enabled: !!eventId,
  });
};

// ------------------------------------------------------------------------

export const useFetchRecentNoShowEventQuery = (eventId?: string) => {
  return useQuery({
    queryKey: eventQueryKeys.getRecentNoShowEvent(),
    queryFn: () =>
      eventApi.get<ApiResponseDto<ResponseEventLastNoShow[]>>(
        "/no-show/detail",
      ),
    select: ({ data }) => data,
    enabled: !!eventId,
  });
};
